import { db } from '../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, arrayUnion, getDoc } from 'firebase/firestore';

// Collection name
const SHOPS_COLLECTION = 'shops';

// Shop Management
export const getShopsByUserId = async (userId) => {
  try {
    const shopsRef = collection(db, SHOPS_COLLECTION);
    const q = query(shopsRef, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    
    const shops = [];
    querySnapshot.forEach((doc) => {
      shops.push({ id: doc.id, ...doc.data() });
    });
    
    console.log('Retrieved shops from Firebase:', shops);
    return shops;
  } catch (error) {
    console.error('Error loading shops from Firebase:', error);
    throw error;
  }
};

export const saveShop = async (userId, shopData) => {
  try {
    if (!userId) throw new Error('User ID is required');
    if (!shopData) throw new Error('Shop data is required');
    if (!shopData.shop) throw new Error('Shop URL is required');
    if (!shopData.accessToken) throw new Error('Access token is required');

    const shopsRef = collection(db, SHOPS_COLLECTION);
    const newShopData = {
      ...shopData,
      userId,
      createdAt: new Date().toISOString()
    };

    const docRef = await addDoc(shopsRef, newShopData);
    const newShop = { id: docRef.id, ...newShopData };
    console.log('Shop saved successfully to Firebase:', newShop);
    return newShop;
  } catch (error) {
    console.error('Error saving shop to Firebase:', error);
    throw error;
  }
};

export const updateShop = async (shopId, shopData) => {
  try {
    if (!shopId) throw new Error('Shop ID is required');
    
    const shopRef = doc(db, SHOPS_COLLECTION, shopId);
    await updateDoc(shopRef, shopData);
    
    const updatedShop = { id: shopId, ...shopData };
    console.log('Shop updated successfully in Firebase:', updatedShop);
    return updatedShop;
  } catch (error) {
    console.error('Error updating shop in Firebase:', error);
    throw error;
  }
};

export const deleteShop = async (shopId, userId) => {
  try {
    if (!shopId) throw new Error('Shop ID is required');
    if (!userId) throw new Error('User ID is required');
    
    const shopRef = doc(db, SHOPS_COLLECTION, shopId);
    const shopDoc = await getDoc(shopRef);

    if (!shopDoc.exists()) {
      throw new Error('Shop not found');
    }

    const shopData = shopDoc.data();
    if (shopData.userId !== userId) {
      throw new Error('Insufficient permissions to delete this shop');
    }

    await deleteDoc(shopRef);
    console.log('Shop deleted successfully from Firebase');
  } catch (error) {
    console.error('Error deleting shop from Firebase:', error);
    throw error;
  }
};

// Custom Metafields Management
export const addShopMetafield = async (shopId, metafieldData) => {
  try {
    if (!shopId) throw new Error('Shop ID is required');
    if (!metafieldData.namespace) throw new Error('Metafield namespace is required');
    if (!metafieldData.key) throw new Error('Metafield key is required');
    
    const shopRef = doc(db, SHOPS_COLLECTION, shopId);
    const newMetafield = {
      ...metafieldData,
      createdAt: new Date().toISOString()
    };
    
    await updateDoc(shopRef, {
      customMetafields: arrayUnion(newMetafield)
    });
    
    console.log('Shop metafield added successfully:', newMetafield);
    return newMetafield;
  } catch (error) {
    console.error('Error adding shop metafield:', error);
    throw error;
  }
};

export const removeShopMetafield = async (shopId, namespace, key) => {
  try {
    if (!shopId) throw new Error('Shop ID is required');
    if (!namespace) throw new Error('Metafield namespace is required');
    if (!key) throw new Error('Metafield key is required');
    
    const shopRef = doc(db, SHOPS_COLLECTION, shopId);
    
    // Get current metafields
    const shopDoc = await getDoc(shopRef);
    const currentMetafields = shopDoc.data()?.customMetafields || [];
    
    // Filter out the metafield to remove
    const updatedMetafields = currentMetafields.filter(
      mf => !(mf.namespace === namespace && mf.key === key)
    );
    
    await updateDoc(shopRef, {
      customMetafields: updatedMetafields
    });
    
    console.log('Shop metafield removed successfully');
  } catch (error) {
    console.error('Error removing shop metafield:', error);
    throw error;
  }
};

export const getShopMetafields = async (shopId) => {
  try {
    if (!shopId) throw new Error('Shop ID is required');
    
    const shopRef = doc(db, SHOPS_COLLECTION, shopId);
    const shopDoc = await getDoc(shopRef);
    
    if (!shopDoc.exists()) {
      throw new Error('Shop not found');
    }
    
    return shopDoc.data()?.customMetafields || [];
  } catch (error) {
    console.error('Error getting shop metafields:', error);
    throw error;
  }
};
